import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { getMipOptionsForSelect } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import {
	useTranslationCapManage,
	useTranslationDefault,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { CreateTransactionDto } from '@dealstack/shared/lib/common/zod/domain/transaction/transactions.zod';
import {
	CreateTransactionFormDto,
	CreateTransactionFormShape,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/CreateNewTransactionDrawer/createNewTransaction.zod.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import {
	previewTransferCounterpart,
	createInseparableRedemption,
	createPairedIssue,
	createTransaction,
	CreateTransactionRequest,
	previewInseparableRedemption,
} from '@dealstack/frontend/app/services/Transactions.service.ts';
import { getMipsForPersonByDate } from '@dealstack/frontend/app/services/InvestmentProgramme.service.ts';
import {
	CapTableRail,
	CreateInseparableRedemptionDto,
	DataSecurityTct,
	TransactionTct,
	TransactionTitle,
	TransactionType,
} from '@dealstack/shared/lib/generated';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { useTransactionPivRelations } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useTransactionPivRelations';
import { useTransactionPartyDefaults } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useTransactionPartyDefaults';
import {
	getTransactionCostLabel,
	isNomineeIssueSplitEligible,
	isNomineeRedemptionEligible,
	isNomineeTransferInterestEligible,
	useTransactionFieldsVisibility,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useTransactionFieldsVisibility';
import { getTransferInterestOptions } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/getTransactionInterestText.ts';
import {
	buildInseparableRedemptionDisclosure,
	InseparableRedemptionDisclosure,
	toLegFingerprint,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/CreateNewTransactionDrawer/inseparableRedemption.utils.ts';
import { useRedemptionCounterpartPreview } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useRedemptionCounterpartPreview.ts';
import { buildTransferCounterpartDisclosure } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/CreateNewTransactionDrawer/transferCounterpart.utils.ts';
import { transactionMutationError } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/transactionMutationError.ts';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { formatDateToBEIgnoreTimezone } from '@dealstack/shared/lib/format/dates/date';

export enum CREATE_TRANSACTION_FORM_FIELD {
	DATE = 'date',
	SECURITY_ID = 'securityId',
	TYPE = 'type',
	BUYER = 'destinationId',
	SELLER = 'sourceId',
	BOUGHT_FOR = 'boughtForId',
	SOLD_FOR = 'soldForId',
	SELLER_MIP = 'fromMipId',
	BUYER_MIP = 'toMipId',
	NUMBER = 'units',
	PRICE = 'cost',
	ENTITY_ID = 'securityPersonId',
	CONVERSION_TARGET_SECURITY_ID = 'conversionTargetSecurityId',
	TITLE = 'title',
	LEGAL_SAME_AS_BENEFICIAL = 'legalSameAsBeneficial',
	NOMINEE_ID = 'nomineeId',
}

const DEFAULT_FORM_VALUES = {
	[CREATE_TRANSACTION_FORM_FIELD.DATE]: null,
	[CREATE_TRANSACTION_FORM_FIELD.TYPE]: null,
	[CREATE_TRANSACTION_FORM_FIELD.BUYER]: null,
	[CREATE_TRANSACTION_FORM_FIELD.SELLER]: null,
	[CREATE_TRANSACTION_FORM_FIELD.SECURITY_ID]: null,
	[CREATE_TRANSACTION_FORM_FIELD.NUMBER]: 0,
	[CREATE_TRANSACTION_FORM_FIELD.PRICE]: 0,
	[CREATE_TRANSACTION_FORM_FIELD.SELLER_MIP]: null,
	[CREATE_TRANSACTION_FORM_FIELD.BUYER_MIP]: null,
	[CREATE_TRANSACTION_FORM_FIELD.BOUGHT_FOR]: null,
	[CREATE_TRANSACTION_FORM_FIELD.SOLD_FOR]: null,
	[CREATE_TRANSACTION_FORM_FIELD.CONVERSION_TARGET_SECURITY_ID]: null,
	[CREATE_TRANSACTION_FORM_FIELD.TITLE]: null,
} as unknown as CreateTransactionDto;

// CAP-1975: the nominee inputs start at "legal title follows the beneficial buyer", i.e. a plain
// Both-titled issue, so a deal with nominee structures behaves like any other until the user opts in.
const DEFAULT_NOMINEE_FORM_VALUES = {
	[CREATE_TRANSACTION_FORM_FIELD.LEGAL_SAME_AS_BENEFICIAL]: true,
	[CREATE_TRANSACTION_FORM_FIELD.NOMINEE_ID]: null,
};

/**
 * A title that names ONE interest, i.e. one that needs the counterpart resolving. Module scope
 * because the counterpart preview reads it during render, above where the submit handlers live.
 */
const isSingleInterest = (title?: TransactionTitle | null): boolean =>
	!!title && title !== TransactionTitle.BENEFICIAL_INTEREST_AND_LEGAL_TITLE;

const DEFAULT_CREATE_TRANSACTION_FORM_VALUES = {
	...DEFAULT_FORM_VALUES,
	...DEFAULT_NOMINEE_FORM_VALUES,
};

export const useCreateNewTransactionDrawerController = (
	refreshTransactionsList: () => void,
	onClose: (createdTransactionId?: string) => void,
	securities?: DataSecurityTct[]
) => {
	const tCapManage = useTranslationCapManage();
	const tDefault = useTranslationDefault();

	const { refreshCapDetails } = useContext(DealContext);
	const { selectedEntityId, refetchPersons, personsSelectOptions, mipSelectOptions } = useContext(
		CapManageControllerContext
	);

	const createTransactionsRequest = useMutation({
		mutationFn: createTransaction,
	});

	// CAP-1975: atomic nominee paired-issue endpoint (replaces the old two-call sequence).
	const createPairedIssueRequest = useMutation({
		mutationFn: createPairedIssue,
	});

	// CAP-2033: a single-interest redemption has to retire the paired counterpart title too, and the
	// resolver derives which holders that touches. Preview first so the user is told, then write.
	const previewInseparableRedemptionRequest = useMutation({
		mutationFn: previewInseparableRedemption,
		onError: (error: unknown) =>
			transactionMutationError(
				error,
				tDefault,
				tCapManage('toasts.errors.inseparable_redemption_preview_failed')
			),
	});
	const createInseparableRedemptionRequest = useMutation({
		mutationFn: createInseparableRedemption,
		onError: (error: unknown) =>
			transactionMutationError(
				error,
				tDefault,
				tCapManage('toasts.errors.inseparable_redemption_failed')
			),
	});
	// CAP-2035: a single-title TRANSFER writes a linked leg on the other rail that stays hidden from
	// every read surface. Preview it so the user is told before the write, the same contract the
	// redemption path above already honours.
	const previewTransferCounterpartRequest = useMutation({
		mutationFn: previewTransferCounterpart,
		onError: (error: unknown) =>
			transactionMutationError(
				error,
				tDefault,
				tCapManage('toasts.errors.transfer_counterpart_preview_failed')
			),
	});
	const [pendingTransfer, setPendingTransfer] = useState<{
		payload: CreateTransactionRequest;
		disclosure: InseparableRedemptionDisclosure;
		shouldCloseOnSuccess?: boolean;
	} | null>(null);

	const [pendingRedemption, setPendingRedemption] = useState<{
		payload: CreateInseparableRedemptionDto;
		disclosure: InseparableRedemptionDisclosure;
		shouldCloseOnSuccess?: boolean;
	} | null>(null);

	const createTransactionForm = useForm<CreateTransactionFormDto>({
		mode: 'all',
		resolver: zodResolver(
			CreateTransactionFormShape(tCapManage, securities, personsSelectOptions)
		),
		defaultValues: DEFAULT_CREATE_TRANSACTION_FORM_VALUES,
		shouldFocusError: false,
	});

	const sellerId = createTransactionForm.watch(CREATE_TRANSACTION_FORM_FIELD.SELLER);
	const buyerId = createTransactionForm.watch(CREATE_TRANSACTION_FORM_FIELD.BUYER);
	const transactionDate = createTransactionForm.watch(CREATE_TRANSACTION_FORM_FIELD.DATE);
	const securityId = createTransactionForm.watch(CREATE_TRANSACTION_FORM_FIELD.SECURITY_ID);

	const getMipsForPersonByDateRequest = useMutation({
		mutationFn: getMipsForPersonByDate,
	});

	useEffect(() => {
		if (!sellerId || !transactionDate || !securityId) {
			return;
		}
		getMipsForPersonByDateRequest.mutate({
			sellerId: sellerId || '',
			transactionDate: formatDateToBEIgnoreTimezone(transactionDate),
			securityId,
		});
	}, [sellerId, transactionDate, securityId]);

	const transactionType = createTransactionForm.watch(
		CREATE_TRANSACTION_FORM_FIELD.TYPE
	) as TransactionType;
	const selectedSecurity = securities?.find((security) => security.id === securityId);

	// CAP-1062: the feature flag keeps every nominee surface dark — no nominee drawer fields and
	// no legal/beneficial title stamped on submitted transactions.
	const nomineeStructuresEnabled = useDealstackFeatureFlag(
		FEATURE_FLAG.CAP_NOMINEE_STRUCTURES
	).value;

	// CAP-1975: whether to surface the legal/beneficial (nominee) split UI for this issue.
	const showNomineeFields =
		nomineeStructuresEnabled && isNomineeIssueSplitEligible(transactionType, selectedSecurity);
	// Switch ON (default) => legal holder == beneficial buyer (a plain "Both" issue).
	// Switch OFF => the `nomineeId` field names the legal-title holder, and the schema requires it.
	const legalSameAsBeneficial = createTransactionForm.watch(
		CREATE_TRANSACTION_FORM_FIELD.LEGAL_SAME_AS_BENEFICIAL
	);

	const setLegalSameAsBeneficial = (next: boolean) => {
		createTransactionForm.setValue(
			CREATE_TRANSACTION_FORM_FIELD.LEGAL_SAME_AS_BENEFICIAL,
			next,
			{
				shouldValidate: true,
			}
		);
		if (next) {
			// The nominee is irrelevant while legal title follows the beneficial buyer, so drop any
			// error raised against it while the switch was off.
			createTransactionForm.clearErrors(CREATE_TRANSACTION_FORM_FIELD.NOMINEE_ID);
		}
	};

	// CAP-1975: a TRANSFER on a nominee security lets the user pick which interest moves
	// (Beneficial / Legal / Both) via the `title` form field, e.g. transferring only the legal title.
	const showTransferInterest =
		nomineeStructuresEnabled &&
		isNomineeTransferInterestEligible(transactionType, selectedSecurity);
	const transferInterestOptions = useMemo(
		() => getTransferInterestOptions(tCapManage),
		[tCapManage]
	);

	// The segmented control has no empty state to fall back on the way the old dropdown's
	// placeholder did — with `title` still null it renders three unselected options and reads as
	// disabled. Seed it with Both, which is what an untouched transfer records anyway.
	useEffect(() => {
		if (
			showTransferInterest &&
			!createTransactionForm.getValues(CREATE_TRANSACTION_FORM_FIELD.TITLE)
		) {
			createTransactionForm.setValue(
				CREATE_TRANSACTION_FORM_FIELD.TITLE,
				TransactionTitle.BENEFICIAL_INTEREST_AND_LEGAL_TITLE
			);
		}
	}, [showTransferInterest]);

	// CAP-2033: a REDEMPTION / CANCELLATION on a nominee security now lets the user choose which
	// interest is retired. It is no longer fixed to the beneficial rail: doing that silently left the
	// counterpart legal title standing over shares that no longer existed, so the two registers
	// disagreed. Choosing a single interest routes the write through the inseparable-redemption
	// resolver, which retires the counterpart title wherever it sits.
	const showRedemptionInterest =
		nomineeStructuresEnabled && isNomineeRedemptionEligible(transactionType, selectedSecurity);

	// "Both" is the safe default: the holder owns both rails over those serials, so the ordinary
	// redemption retires them together and no counterpart has to be derived.
	useEffect(() => {
		if (
			showRedemptionInterest &&
			!createTransactionForm.getValues(CREATE_TRANSACTION_FORM_FIELD.TITLE)
		) {
			createTransactionForm.setValue(
				CREATE_TRANSACTION_FORM_FIELD.TITLE,
				TransactionTitle.BENEFICIAL_INTEREST_AND_LEGAL_TITLE
			);
		}
	}, [showRedemptionInterest]);

	// CAP-2039: who ELSE loses title, resolved while the form is being filled in rather than after
	// Save. Before this the counterpart appeared for the first time in the confirmation modal, so the
	// one fact a user cannot work out for themselves arrived after they had committed to the
	// operation — and a resolver refusal read as the form rejecting them instead of as a fact about
	// the register. The panel is read-only: the counterpart is derived from legal structure, not
	// chosen (see RedemptionCounterpartPanel).
	const redemptionTitle = createTransactionForm.watch(CREATE_TRANSACTION_FORM_FIELD.TITLE);
	const redemptionInterest =
		redemptionTitle === TransactionTitle.LEGAL_TITLE
			? CapTableRail.LEGAL
			: CapTableRail.BENEFICIAL;
	const redemptionCounterpart = useRedemptionCounterpartPreview(
		{
			securityId,
			holderId: sellerId,
			interest: redemptionInterest,
			units: Number(createTransactionForm.watch(CREATE_TRANSACTION_FORM_FIELD.NUMBER) ?? 0),
			cost: Number(createTransactionForm.watch(CREATE_TRANSACTION_FORM_FIELD.PRICE) ?? 0),
			date: transactionDate,
			type: transactionType,
			fromMipId: createTransactionForm.watch(CREATE_TRANSACTION_FORM_FIELD.SELLER_MIP),
			soldForId: createTransactionForm.watch(CREATE_TRANSACTION_FORM_FIELD.SOLD_FOR),
		},
		showRedemptionInterest && isSingleInterest(redemptionTitle)
	);

	const handleCreateSuccess = (
		createdTransaction: TransactionTct | undefined,
		shouldCloseOnSuccess?: boolean
	) => {
		createTransactionForm.reset({ ...DEFAULT_CREATE_TRANSACTION_FORM_VALUES });

		if (shouldCloseOnSuccess) {
			onClose(createdTransaction?.id);
		} else {
			showToastV2({
				title: tCapManage('toasts.success.transaction_created_successfully'),
			});
		}

		void refreshCapDetails();
		refreshTransactionsList();
		void refetchPersons();
	};

	// CAP-1975 / CAP-1976: delegates to the atomic paired-issue endpoint — the backend writes TWO
	// ISSUEs from the issuer inside one transaction (NomineeTransactionService.createPairedIssue),
	// LEGAL_TITLE to the nominee and BENEFICIAL_INTEREST to the beneficial buyer, linked by one
	// interestPairId so the fold co-numbers them onto a single serial range. Neither leg is a
	// transfer: nothing moved between the two holders. The legal leg carries the MIP/PIV context and
	// zero consideration; the beneficial leg carries the cost (all handled server-side).
	const createPairedNomineeIssue = async (
		basePayload: CreateTransactionRequest,
		legalHolderId: string,
		beneficialBuyerId: string,
		shouldCloseOnSuccess?: boolean
	) => {
		try {
			const [legalIssue] = await createPairedIssueRequest.mutateAsync({
				date: basePayload.date,
				securityId: basePayload.securityId,
				units: basePayload.units,
				cost: basePayload.cost,
				nomineeId: legalHolderId,
				beneficialBuyerId,
				// The holding (programme) is shared — one share, one programme. Bought-for is per
				// interest and each leg is validated against its own destination, so the beneficial
				// holder's is never reused for the legal one: the drawer collects only the beneficial
				// holder's, and the legal leg goes without.
				toMipId: basePayload.toMipId ?? undefined,
				beneficialBoughtForId: basePayload.boughtForId ?? undefined,
			});
			handleCreateSuccess(legalIssue, shouldCloseOnSuccess);
		} catch {
			showToastV2({
				state: 'error',
				message: tCapManage('toasts.errors.transaction_paired_issue_failed', {
					defaultValue:
						'Could not create the paired legal/beneficial transactions for this nominee issue',
				}),
			});
		}
	};

	const holderNameById = (id: string): string =>
		personsSelectOptions?.find((option) => option.value === id)?.label ??
		tCapManage('transactions.inseparable_redemption.unknown_holder');

	/**
	 * CAP-2033: resolve the counterpart legs without writing, then hand them to the confirmation.
	 * A rejection here (120246-120249) is surfaced as-is — the resolver's wording already explains
	 * why the shares cannot be retired, and it is the same answer the write would give.
	 */
	const previewSingleInterestRedemption = async (
		formValues: CreateTransactionFormDto,
		shouldCloseOnSuccess?: boolean
	) => {
		const interest =
			formValues.title === TransactionTitle.LEGAL_TITLE
				? CapTableRail.LEGAL
				: CapTableRail.BENEFICIAL;
		// The redeeming holder is the SELLER: a redemption takes the shares back off them.
		const payload: CreateInseparableRedemptionDto = {
			date: formatDateToBEIgnoreTimezone(formValues.date),
			securityId: formValues.securityId,
			holderId: formValues.sourceId || '',
			interest,
			units: Number(formValues.units),
			cost: Number(formValues.cost),
			// The resolver retires a cancellation on the same terms as a redemption, but it records
			// the type it is given — so a cancellation has to say so, or it is written as a redemption.
			type:
				formValues.type === TransactionType.CANCELLATION
					? TransactionType.CANCELLATION
					: TransactionType.REDEMPTION,
			// Omitted rather than null: the resolver reads "did the caller name this axis?" from key
			// presence, and leaving both unset is what lets it span every position the holder has.
			...(formValues.fromMipId ? { fromMipId: formValues.fromMipId } : {}),
			...(formValues.soldForId ? { soldForId: formValues.soldForId } : {}),
		};

		try {
			const preview = await previewInseparableRedemptionRequest.mutateAsync(payload);
			const legs = preview?.legs ?? [];
			if (!legs.length) {
				showToastV2({
					state: 'error',
					message: tCapManage('toasts.errors.inseparable_redemption_no_legs'),
				});
				return;
			}
			setPendingRedemption({
				// CAP-2039: the write carries the legs it just disclosed, so it can be refused
				// (120254) if a re-resolve no longer says the same thing. The resolver reads live
				// holdings, so without this a register that moved between the disclosure and the
				// confirmation retired counterparts the user was never told about.
				payload: { ...payload, disclosedLegs: legs.map(toLegFingerprint) },
				shouldCloseOnSuccess,
				disclosure: buildInseparableRedemptionDisclosure(
					legs,
					interest,
					selectedSecurity?.name ?? '',
					holderNameById,
					tCapManage
				),
			});
		} catch {
			// The mutation's own onError has already told the user; this only stops the flow.
		}
	};

	const confirmInseparableRedemption = async () => {
		if (!pendingRedemption) {
			return;
		}
		try {
			const [firstLeg] = await createInseparableRedemptionRequest.mutateAsync(
				pendingRedemption.payload
			);
			const shouldClose = pendingRedemption.shouldCloseOnSuccess;
			setPendingRedemption(null);
			handleCreateSuccess(firstLeg, shouldClose);
		} catch {
			// Reported by the mutation's onError; the confirmation stays open on the entered values.
		}
	};

	const cancelInseparableRedemption = () => setPendingRedemption(null);

	const previewSingleInterestTransfer = async (
		payload: CreateTransactionRequest,
		shouldCloseOnSuccess?: boolean
	) => {
		try {
			const preview = await previewTransferCounterpartRequest.mutateAsync(payload);
			const disclosure = preview
				? buildTransferCounterpartDisclosure(
						preview,
						payload.title,
						selectedSecurity?.name ?? '',
						payload.sourceId,
						holderNameById,
						(units) => units.toLocaleString(),
						tCapManage
					)
				: null;
			// Nothing to disclose (Both-titled, non-nominee, or no counterpart holder): the write is
			// an ordinary single-row transfer, so do not invent a confirmation for it.
			if (!disclosure) {
				createTransactionsRequest.mutate(payload, {
					onSuccess: (createdTransaction: TransactionTct) =>
						handleCreateSuccess(createdTransaction, shouldCloseOnSuccess),
				});
				return;
			}
			setPendingTransfer({ payload, disclosure, shouldCloseOnSuccess });
		} catch {
			// The mutation's own onError has already told the user; this only stops the flow.
		}
	};

	const confirmTransferCounterpart = () => {
		if (!pendingTransfer) {
			return;
		}
		const { payload, shouldCloseOnSuccess } = pendingTransfer;
		createTransactionsRequest.mutate(payload, {
			onSuccess: (createdTransaction: TransactionTct) => {
				setPendingTransfer(null);
				handleCreateSuccess(createdTransaction, shouldCloseOnSuccess);
			},
		});
	};

	const cancelTransferCounterpart = () => setPendingTransfer(null);

	const onSubmit = (shouldCloseOnSuccess?: boolean) => (formValues: CreateTransactionFormDto) => {
		const basePayload: CreateTransactionRequest = {
			type: (formValues.type as TransactionType) || null,
			date: formatDateToBEIgnoreTimezone(formValues.date),
			securityId: formValues.securityId,
			units: [
				TransactionType.SHAREHOLDER_CONTRIBUTION,
				TransactionType.REDUCTION_IN_SHARE_CAPITAL,
				TransactionType.DIVIDEND,
				TransactionType.CONVERSION_SHARE_CAPITAL,
			].includes(formValues.type as TransactionType)
				? 0
				: Number(formValues.units),
			cost: Number(formValues.cost),
			sourceId: formValues.sourceId || '',
			destinationId: formValues.destinationId || '',
			soldForId: formValues.soldForId,
			boughtForId: formValues.boughtForId,
			fromMipId: formValues.fromMipId || undefined,
			toMipId: formValues.toMipId || undefined,
			conversionTargetSecurityId: formValues.conversionTargetSecurityId || undefined,
		};

		const beneficialBuyerId = formValues.destinationId || '';
		// CAP-1975: an eligible issue with the "Same as beneficial interest?" switch off routes through
		// the paired-issue endpoint. The schema (see createNewTransaction.zod.ts) guarantees a nominee
		// distinct from the beneficial buyer here, so there is no silent fall-through to a plain issue.
		if (showNomineeFields && !formValues.legalSameAsBeneficial && formValues.nomineeId) {
			void createPairedNomineeIssue(
				basePayload,
				formValues.nomineeId,
				beneficialBuyerId,
				shouldCloseOnSuccess
			);
			return;
		}

		// CAP-2033: a nominee redemption of ONE interest cannot be written directly — the counterpart
		// title has to be retired over the same serials, and only the resolver knows who holds it.
		// Preview it, disclose the legs, and let the confirmation do the write.
		if (showRedemptionInterest && isSingleInterest(formValues.title)) {
			void previewSingleInterestRedemption(formValues, shouldCloseOnSuccess);
			return;
		}

		// On a nominee issue where the legal holder matches the beneficial buyer, stamp the
		// explicit "Both" title. On a nominee TRANSFER or REDEMPTION, stamp the interest the user
		// chose (Beneficial / Legal / Both) — a "Both" redemption retires both rails on its own.
		// Non-nominee transactions keep the default (omitted) title.
		let title: TransactionTitle | undefined;
		if (showNomineeFields) {
			title = TransactionTitle.BENEFICIAL_INTEREST_AND_LEGAL_TITLE;
		} else if (showTransferInterest || showRedemptionInterest) {
			title = formValues.title ?? undefined;
		} else {
			title = undefined;
		}

		const payload = { ...basePayload, title };

		// CAP-2035: a single-title transfer on a nominee security writes a hidden linked leg, so it
		// goes through the preview to be disclosed first. The preview answers "no counterpart" for
		// everything else and submits straight through, so this is not a new hop for ordinary rows.
		if (showTransferInterest && isSingleInterest(title)) {
			void previewSingleInterestTransfer(payload, shouldCloseOnSuccess);
			return;
		}

		createTransactionsRequest.mutate(payload, {
			onSuccess: (createdTransaction: TransactionTct) =>
				handleCreateSuccess(createdTransaction, shouldCloseOnSuccess),
		});
	};

	const sellerMipSelectOptions = useMemo(
		() => getMipOptionsForSelect(getMipsForPersonByDateRequest.data),
		[getMipsForPersonByDateRequest.data]
	);

	const { hideSeller, hideBuyer, hideBuyerMip, hideSellerMip, hideUnits, hideCost } =
		useTransactionFieldsVisibility(transactionType, selectedSecurity?.issuedIn);

	const { hideBoughtFor, hideSoldFor, boughtForOptions, soldForOptions } =
		useTransactionPivRelations({
			buyerId,
			sellerId,
			personsSelectOptions,
			setValue: createTransactionForm.setValue,
			getValues: createTransactionForm.getValues,
			boughtForField: CREATE_TRANSACTION_FORM_FIELD.BOUGHT_FOR,
			soldForField: CREATE_TRANSACTION_FORM_FIELD.SOLD_FOR,
		});

	const { unitsDecimalPoints } = useTransactionPartyDefaults({
		transactionType,
		selectedSecurity,
		issuerId: selectedEntityId,
		getValues: createTransactionForm.getValues,
		setValue: createTransactionForm.setValue,
		clearErrors: createTransactionForm.clearErrors,
		fields: {
			buyer: CREATE_TRANSACTION_FORM_FIELD.BUYER,
			seller: CREATE_TRANSACTION_FORM_FIELD.SELLER,
			price: CREATE_TRANSACTION_FORM_FIELD.PRICE,
			units: CREATE_TRANSACTION_FORM_FIELD.NUMBER,
		},
	});

	return {
		sellerMipSelectOptions,
		createTransactionsRequest,
		createPairedIssueRequest,
		onSubmit,
		createTransactionForm,
		refetchPersons,
		personsSelectOptions,
		mipSelectOptions,
		selectedSecurity,
		transactionType,
		showNomineeFields,
		legalSameAsBeneficial,
		setLegalSameAsBeneficial,
		showTransferInterest,
		transferInterestOptions,
		showRedemptionInterest,
		redemptionCounterpart,
		redemptionInterest,
		pendingRedemption,
		confirmInseparableRedemption,
		cancelInseparableRedemption,
		isRedeeming:
			previewInseparableRedemptionRequest.isPending ||
			createInseparableRedemptionRequest.isPending,
		pendingTransfer,
		confirmTransferCounterpart,
		cancelTransferCounterpart,
		isTransferring:
			previewTransferCounterpartRequest.isPending || createTransactionsRequest.isPending,
		hideSeller,
		hideBuyer,
		hideBuyerMip,
		hideSellerMip,
		hideBoughtFor,
		hideSoldFor,
		soldForOptions,
		boughtForOptions,
		costLabel: getTransactionCostLabel(transactionType),
		hideUnits,
		unitsDecimalPoints,
		hideCost,
	};
};
